import type { FeatureFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { FeatureBadgeEntry } from '@b-stock/fe-core/bstock-react'

import { FeatureFlags } from '@/helpers/featureFlags'

/** A "Beta" badge: no date, its own flag shows it. */
export type BetaBadgeEntry = { kind: 'beta'; flag: FeatureFlag<boolean> }

/**
 * Launches that show a "New" or "Beta" badge, one entry per surface.
 *
 * - Keys are snake_case analytics ids (`badge_feature_key`): never rename or
 *   reuse one.
 * - `launchDate` is the moment the badge first renders (ISO with a time and an
 *   offset). The badge hides itself `windowDays` (default 21) later. If go-live
 *   slips, move `launchDate`, not the deploy.
 * - To badge a control, render `<FeatureBadgeSlot featureKey="…" />` beside
 *   it. When the surface also needs to know whether the pill is on screen (to
 *   report it), call fe-core `useFeatureBadge(FEATURE_BADGES.key)` once and
 *   render `<FeatureBadge>` from that result, so both agree.
 * - An A/B-tested badge spreads `featureBadgeEventProps` into the surface's
 *   events in both arms (see `./analytics`). Its flag alone shows the pill, so
 *   QA can force it before `launchDate`, and the experiment's start and stop
 *   are its window. The date only feeds `badge_launch_date`; the `launchDate`,
 *   reload and delete-on-expiry rules in this comment don't apply to it.
 *   Remove its entry with its flag.
 * - The page view lists every badge on screen in `feature_badges`. The Slot
 *   reports itself; a surface that renders `<FeatureBadge>` directly calls
 *   `useReportPageFeatureBadge` (see `./pageFeatureBadges`).
 * - On a dark host (a primary button) pass `onDark` to `<FeatureBadge>`.
 * - Limits: `feature_badges` lists only badges on screen in the page's first
 *   commit, so a badge behind `Suspense` or that turns on later is left out.
 *   Pages Router pages have no server clock, so the badge pops in after mount
 *   there. A tab opened before `launchDate` shows it only after a reload.
 * - Delete the entry once it expires; `constants.test.ts` fails on stale,
 *   far-future and misnamed entries. `git log -p` on this file is the launch
 *   history.
 * - A Beta entry has no `launchDate`: its `flag` shows the pill and the Slot
 *   reads it, so render the Slot only where the feature itself is visible (a
 *   flag read is a LaunchDarkly exposure). Tag the surface's own events with
 *   `useFeatureBadgeEventProps(key)`. Never give one feature both a New
 *   and a Beta entry. A New A/B badge has no `flag` field; its surface still
 *   reads its own flag (`showBadge`, as the Recent Views carousel does).
 * - How to badge a launch end to end: fe-core Storybook, Library/Atoms/
 *   FeatureBadge/How to badge your launch. PM overview and playbook links:
 *   https://bstock.atlassian.net/wiki/spaces/EN/pages/4588404739
 */
export const FEATURE_BADGES = {
  recent_views_carousel: {
    kind: 'new',
    launchDate: '2026-10-05T09:00:00-04:00',
  },
  compare_listings: {
    kind: 'beta',
    flag: FeatureFlags.BETA_BADGE_COMPARE_LISTINGS,
  },
} as const satisfies Record<string, FeatureBadgeEntry | BetaBadgeEntry>

export type FeatureBadgeKey = keyof typeof FEATURE_BADGES
